import { Autocomplete, Chip, CircularProgress, TextField, TextFieldProps } from "@mui/material";
import { useState } from "react";

type MultiValueTextInputProps = Omit<
  TextFieldProps,
  "value" | "defaultValue" | "onChange" | "onKeyDown"
> & {
  value?: string[] | null;
  onChange: (values: string[]) => void;
  options?: string[];
  maxSuggestions?: number;
  loading?: boolean;
  onInputChange?: (input: string) => void;
  onRequestApply?: () => void;
  parseInput?: (input: string) => string[];
  autoSelect?: boolean;
};

function singleValue(input: string): string[] {
  const value = input.trim();

  return value ? [value] : [];
}

export function MultiValueTextInput({
  value,
  onChange,
  options = [],
  maxSuggestions,
  loading = false,
  onInputChange,
  onRequestApply,
  parseInput = singleValue,
  autoSelect = false,
  size = "small",
  disabled,
  ...props
}: MultiValueTextInputProps) {
  const [inputValue, setInputValue] = useState("");

  const updateInput = (input: string) => {
    setInputValue(input);
    onInputChange?.(input);
  };

  const updateValues = (values: string[], input = "") => {
    const uniqueValues = [...new Set(values)];

    setInputValue(input);
    onInputChange?.(input);
    onChange(uniqueValues);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect={autoSelect}
      size={size}
      disabled={disabled}
      options={options}
      value={value ?? []}
      inputValue={inputValue}
      loading={loading}
      onInputChange={(_, input, reason) => {
        if (reason !== "reset") updateInput(input);
      }}
      onChange={(event, values, reason) => {
        if (reason === "removeOption") {
          const hasPendingInput = inputValue.trim().length > 0;
          const isBlur = event?.type === "blur";
          const isEnter = event?.type === "keydown" && "key" in event && event.key === "Enter";

          // MUI can interpret committing text as deselecting a highlighted chip.
          const shouldPreserveSelection = isBlur || (isEnter && hasPendingInput);

          if (shouldPreserveSelection && hasPendingInput) {
            const pendingValues = parseInput(inputValue);

            updateValues([...(value ?? []), ...pendingValues]);
          }

          if (shouldPreserveSelection) return;
        }

        const addedValue = values[values.length - 1] ?? "";

        if (
          reason === "createOption" ||
          reason === "blur" ||
          (reason === "selectOption" && !options.includes(addedValue))
        ) {
          updateValues([...values.slice(0, -1), ...parseInput(addedValue)]);
        } else {
          updateValues(values, reason === "removeOption" ? inputValue : "");
        }
      }}
      renderInput={(params) => (
        <TextField
          {...props}
          {...params}
          disabled={disabled}
          autoComplete="off"
          onKeyDown={(event) => {
            const hasInput = !!inputValue.trim();

            if (hasInput && (event.key === "," || event.key === "Tab")) {
              if (event.key !== "Tab") event.preventDefault();
              updateValues([...(value ?? []), ...parseInput(inputValue)]);
            } else if (event.key === "Enter" && !hasInput && onRequestApply) {
              event.preventDefault();
              onRequestApply();
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            autoComplete: "off",
            "data-lpignore": "true",
            "data-form-type": "other",
          }}
        />
      )}
      renderTags={(values, getTagProps) =>
        values.map((entry, index) => {
          const { key, ...chipProps } = getTagProps({ index });

          return <Chip key={key} size="small" label={entry} {...chipProps} />;
        })
      }
      filterOptions={(availableOptions, state) => {
        const input = state.inputValue.trim();

        const inputLower = input.toLowerCase();

        const filtered = availableOptions
          .filter((option) => option.toLowerCase().includes(inputLower))
          .slice(0, maxSuggestions);

        if (input && !availableOptions.some((option) => option.toLowerCase() === inputLower)) {
          if (maxSuggestions && filtered.length === maxSuggestions) filtered.pop();

          filtered.push(input);
        }

        return filtered;
      }}
    />
  );
}
